import React from 'react';
import { Box, Typography, Button, SxProps, Theme } from '@mui/material';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface EmptyStateProps {
  /** Optional icon shown in the circular badge */
  icon?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  /** Primary call-to-action button */
  action?: EmptyStateAction;
  /** Compact padding for smaller containers (tabs, panels) */
  dense?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Reusable empty-state panel for "nothing here yet" situations.
 *
 * Use this instead of an info Alert when a list/area is simply empty — it reads
 * as an invitation (dashed panel, icon badge, headline, CTA) rather than a
 * notice. Keep using Alert for real errors, warnings, and "no data for your
 * filter" messages.
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  dense = false,
  sx,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        py: dense ? { xs: 3, sm: 4 } : { xs: 6, sm: 9 },
        px: 3,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 3,
        bgcolor: 'background.paper',
        ...sx,
      }}
    >
      {icon && (
        <Box
          sx={{
            width: dense ? 48 : 56,
            height: dense ? 48 : 56,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            mb: 2,
            '& svg': { fontSize: dense ? 24 : 28 },
          }}
        >
          {icon}
        </Box>
      )}
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.75, maxWidth: 440, lineHeight: 1.6 }}
        >
          {description}
        </Typography>
      )}
      {action && (
        <Button
          variant="contained"
          startIcon={action.icon}
          onClick={action.onClick}
          disabled={action.disabled}
          sx={{ mt: 3 }}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );
};

export default EmptyState;
